import { useCallback, useRef } from 'react';
import type { WindowPosition, WindowSize } from './types';
import { MAP_TOP_OFFSET } from './clampWindowToViewport';

interface UseWindowResizeOptions {
  enabled: boolean;
  size: WindowSize;
  position: WindowPosition;
  windowRef: React.RefObject<HTMLDivElement>;
  setSize: (s: WindowSize) => void;
  onStart?: () => void;
  /** Height of the header bar, so the content div can be resized in lock-step with the outer */
  headerHeight: number;
}

function clampSize(nextW: number, nextH: number, position: WindowPosition): WindowSize {
  const minW = window.innerWidth <= 480 ? 280 : 400;
  const minH = 450;
  // Resize handle is bottom-right; cap size so the entire window stays inside
  // the map area (top inset = TopMenuBar). #504 follow-up.
  const topRef = Math.max(position.y, MAP_TOP_OFFSET);
  const maxW = Math.max(minW, window.innerWidth - position.x);
  const maxH = Math.max(minH, window.innerHeight - topRef);
  return {
    width: Math.max(minW, Math.min(maxW, nextW)),
    height: Math.max(minH, Math.min(maxH, nextH)),
  };
}

// Content div uses a fixed pixel height tied to React state so its size is
// stable across minimize/restore. During a drag we mutate the DOM directly,
// so we also have to update the content element's height — otherwise the
// outer grows but the inner stays at the old size and you see the panel
// background behind the content until mouseup commits new state.
function updateContentSize(root: HTMLElement, height: number, headerHeight: number) {
  const content = root.querySelector<HTMLElement>(':scope > .window-content');
  if (content) {
    content.style.height = `${Math.max(0, height - headerHeight)}px`;
  }
}

export function useWindowResize({ enabled, size, position, windowRef, setSize, onStart, headerHeight }: UseWindowResizeOptions) {
  const isResizingRef = useRef(false);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  const stateRef = useRef({ size, position });
  stateRef.current = { size, position };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;
    e.preventDefault();
    e.stopPropagation();
    onStart?.();

    isResizingRef.current = true;
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: stateRef.current.size.width,
      height: stateRef.current.size.height,
    };
    document.body.classList.add('window-resizing');
    if (windowRef.current) {
      windowRef.current.classList.add('window-resizing-active');
      // Disable the minimize/restore CSS height transition during drag so
      // the outer matches our DOM-direct style updates instantly. Without
      // this, the outer animates toward the new height over 200ms while
      // the inner content snaps immediately — visible as a black gap
      // (panel bg) especially on shrink.
      windowRef.current.style.transition = 'none';
    }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizingRef.current || !resizeStartRef.current || !windowRef.current) return;
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const deltaX = moveEvent.clientX - resizeStartRef.current.x;
      const deltaY = moveEvent.clientY - resizeStartRef.current.y;
      const next = clampSize(
        resizeStartRef.current.width + deltaX,
        resizeStartRef.current.height + deltaY,
        stateRef.current.position,
      );
      // DOM-direct during drag — no React re-render until commit on mouseup
      windowRef.current.style.width = `${next.width}px`;
      windowRef.current.style.height = `${next.height}px`;
      updateContentSize(windowRef.current, next.height, headerHeight);
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      upEvent.stopPropagation();

      if (windowRef.current) {
        const finalW = parseInt(windowRef.current.style.width) || stateRef.current.size.width;
        const finalH = parseInt(windowRef.current.style.height) || stateRef.current.size.height;
        setSize({ width: finalW, height: finalH });
        // Inline content height style was set during drag; clear it so React's
        // state-driven `height: contentHeight` takes over cleanly on next render.
        const content = windowRef.current.querySelector<HTMLElement>(':scope > .window-content');
        if (content) content.style.height = '';
      }

      isResizingRef.current = false;
      resizeStartRef.current = null;
      document.body.classList.remove('window-resizing');
      if (windowRef.current) {
        windowRef.current.classList.remove('window-resizing-active');
        windowRef.current.style.transition = '';
      }
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
    };

    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
  }, [enabled, onStart, setSize, windowRef]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;
    e.stopPropagation();
    onStart?.();

    const windowEl = windowRef.current;
    if (!windowEl) return;

    const touch = e.touches[0];
    isResizingRef.current = true;
    resizeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      width: stateRef.current.size.width,
      height: stateRef.current.size.height,
    };
    document.body.classList.add('window-resizing');
    windowEl.classList.add('window-resizing-active');
    windowEl.style.transition = 'none';

    const handleTouchMove = (moveEvent: TouchEvent) => {
      if (!isResizingRef.current || !resizeStartRef.current) return;
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const t = moveEvent.touches[0];
      const deltaX = t.clientX - resizeStartRef.current.x;
      const deltaY = t.clientY - resizeStartRef.current.y;
      const next = clampSize(
        resizeStartRef.current.width + deltaX,
        resizeStartRef.current.height + deltaY,
        stateRef.current.position,
      );
      windowEl.style.width = `${next.width}px`;
      windowEl.style.height = `${next.height}px`;
      updateContentSize(windowEl, next.height, headerHeight);
    };

    const handleTouchEnd = () => {
      const finalW = parseInt(windowEl.style.width) || stateRef.current.size.width;
      const finalH = parseInt(windowEl.style.height) || stateRef.current.size.height;
      setSize({ width: finalW, height: finalH });
      const content = windowEl.querySelector<HTMLElement>(':scope > .window-content');
      if (content) content.style.height = '';

      isResizingRef.current = false;
      resizeStartRef.current = null;
      document.body.classList.remove('window-resizing');
      windowEl.classList.remove('window-resizing-active');
      windowEl.style.transition = '';
      windowEl.removeEventListener('touchmove', handleTouchMove);
      windowEl.removeEventListener('touchend', handleTouchEnd);
    };

    windowEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    windowEl.addEventListener('touchend', handleTouchEnd);
  }, [enabled, onStart, setSize, windowRef]);

  return { handleMouseDown, handleTouchStart };
}
